import { supabase, getUserRole, clearUserRoleCache } from '../config/supabase.js'
import { getAccessDescription } from '../utils/helpers.js'
import { initRouter } from '../router/router.js'
import { loadPersonListData } from '../person/person-list.js'
import { updateCrypticLine } from '../utils/keepalive.js'
import './auth.css'

let isPasswordMode = false

export function showAuthPage() {
  document.getElementById('auth-page').classList.remove('hidden')
  document.getElementById('app-page').classList.add('hidden')
  renderLoginForm()
}

export function showApp() {
  document.getElementById('auth-page').classList.add('hidden')
  document.getElementById('app-page').classList.remove('hidden')
  updateUserInfo()
  initRouter()
}

export async function checkAuth() {
  const { data: { session } } = await supabase.auth.getSession()
  
  if (session) {
    await handleSuccessfulLogin(session.user)
    showApp()
    await loadPersonListData()
  } else {
    showAuthPage()
  }
  
  // Listen for auth changes
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' && session) {
      await handleSuccessfulLogin(session.user)
      showApp()
      await loadPersonListData()
    } else if (event === 'SIGNED_OUT') {
      showAuthPage()
    }
  })
}

async function updateUserInfo() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return
  
  document.getElementById('user-email').textContent = user.email
  
  const role = await getUserRole()
  const roleElement = document.getElementById('user-role')
  roleElement.textContent = role
  
  // Color code roles
  roleElement.style.background = 
    role === 'admin' ? '#f44336' :
    role === 'family' ? '#4CAF50' :
    '#2196F3'
  
  // Add access level info
  const existingAccessInfo = document.querySelector('.access-info')
  if (existingAccessInfo) {
    existingAccessInfo.remove()
  }
  
  const accessInfo = document.createElement('div')
  accessInfo.className = 'access-info'
  accessInfo.style.cssText = 'font-size: 12px; color: #666; margin-top: 10px;'
  accessInfo.innerHTML = getAccessDescription(role)
  document.querySelector('.header').appendChild(accessInfo)
}

export async function handleLogout() {
  await supabase.auth.signOut()
  clearUserRoleCache()
  showAuthPage()
}

function renderLoginForm() {
  const authPage = document.getElementById('auth-page')
  
  authPage.innerHTML = `
    <div class="auth-container">
      <h1>Family Tree</h1>
      <p>Welcome back</p>
      
      <div id="auth-message"></div>
      
      <form id="auth-form" class="auth-form">
        <div class="form-group">
          <label for="email">Email</label>
          <div style="display: flex; align-items: center; gap: 10px;">
            <input 
              type="email" 
              id="email" 
              name="email" 
              required 
              autocomplete="email"
              placeholder="your.email@example.com"
              style="flex: 1;"
            >
            <button type="button" id="change-email-btn" class="btn-text-small" style="display: none; white-space: nowrap;">Change</button>
          </div>
        </div>
        
        <div id="password-section" class="form-group" style="display: none;">
          <label for="password">Password</label>
          <input 
            type="password" 
            id="password" 
            name="password" 
            autocomplete="current-password"
            placeholder="Enter your password"
          >
        </div>
        
        <button type="submit" id="auth-submit" class="auth-btn">
          Continue
        </button>
        
        <div class="auth-toggle" style="display: none;">
          <button type="button" id="toggle-mode-btn">or use password instead</button>
        </div>
      </form>

      <p id="cryptic-line" class="cryptic-line"></p>
    </div>
  `

  setupLoginListeners()
  updateCrypticLine()
}

function setupLoginListeners() {
  const form = document.getElementById('auth-form')
  const emailInput = document.getElementById('email')
  const passwordInput = document.getElementById('password')
  const passwordSection = document.getElementById('password-section')
  const submitBtn = document.getElementById('auth-submit')
  const toggleBtn = document.getElementById('toggle-mode-btn')
  const toggleDiv = document.querySelector('.auth-toggle')
  const messageDiv = document.getElementById('auth-message')
  const changeEmailBtn = document.getElementById('change-email-btn')
  
  let emailStep = true // Track which step we're on
  let userRole = null
  
  // Change email button
  changeEmailBtn.addEventListener('click', () => {
    // Reset to step 1
    emailStep = true
    userRole = null
    isPasswordMode = false
    
    emailInput.disabled = false
    emailInput.focus()
    passwordSection.style.display = 'none'
    toggleDiv.style.display = 'none'
    submitBtn.textContent = 'Continue'
    changeEmailBtn.style.display = 'none'
    messageDiv.innerHTML = ''
    passwordInput.value = ''
  })
  
  // Toggle between magic link and password mode (only for non-admins)
  toggleBtn.addEventListener('click', () => {
    isPasswordMode = !isPasswordMode
    
    if (isPasswordMode) {
      passwordSection.style.display = 'block'
      submitBtn.textContent = 'Login with Password'
      toggleBtn.textContent = 'or use magic link instead'
      passwordInput.required = true
    } else {
      passwordSection.style.display = 'none'
      submitBtn.textContent = 'Send Magic Link'
      toggleBtn.textContent = 'or use password instead'
      passwordInput.required = false
      messageDiv.innerHTML = ''
    }
  })
  
  // Handle form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    
    const email = emailInput.value.trim()
    
    // Disable button during processing
    submitBtn.disabled = true
    const originalText = submitBtn.textContent
    submitBtn.textContent = 'Processing...'
    messageDiv.innerHTML = ''
    
    try {
      if (emailStep) {
        // STEP 1: Check user role
        emailInput.disabled = true
        changeEmailBtn.style.display = 'inline-block'
        userRole = await checkIfAdmin(email)
        
        console.log('After checkIfAdmin, userRole is:', userRole)
        
        if (userRole) {
          // Admin: Show password field only
          console.log('Showing admin password login')
          passwordSection.style.display = 'block'
          submitBtn.textContent = 'Login with Password'
          passwordInput.required = true
          passwordInput.focus()
          toggleDiv.style.display = 'none'
        } else {
          // Non-admin: Show magic link (primary) with password toggle
          console.log('Showing non-admin magic link login')
          submitBtn.textContent = 'Send Magic Link'
          toggleDiv.style.display = 'block'
          isPasswordMode = false
        }
        
        emailStep = false
        
      } else {
        // STEP 2: Actually login
        const password = passwordInput.value
        
        if (userRole || isPasswordMode) {
          // Password login
          if (!password) {
            messageDiv.innerHTML = '<div class="error-message">Please enter your password</div>'
            submitBtn.disabled = false
            submitBtn.textContent = originalText
            return
          }
          await loginWithPassword(email, password)
          messageDiv.innerHTML = '<div class="success-message">Welcome back!</div>'
        } else {
          // Magic link
          await sendMagicLink(email)
          messageDiv.innerHTML = `
            <div class="success-message">
              <strong>Check your email!</strong><br>
              We sent you a magic link to log in.
            </div>
          `
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      messageDiv.innerHTML = `<div class="error-message">${error.message}</div>`
    } finally {
      submitBtn.disabled = false
      if (emailStep === false && submitBtn.textContent === 'Processing...') {
        submitBtn.textContent = originalText
      }
    }
  })
}

async function checkIfAdmin(email) {
  // Query user_profile to check role
  const { data, error } = await supabase
    .from('user_profile')
    .select('role, email')
    .eq('email', email)
    .maybeSingle()
  
  console.log('Admin check for email:', email)
  console.log('Query result:', data)
  console.log('Query error:', error)
  
  if (error) {
    console.error('Error checking user role:', error)
    return false
  }
  
  if (!data) {
    console.log('No user profile found for this email')
    return false
  }
  
  const isAdmin = data?.role === 'admin'
  console.log('Is admin?', isAdmin)
  
  return isAdmin
}

async function loginWithPassword(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  
  if (error) throw error
}

async function sendMagicLink(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: window.location.origin
    }
  })
  
  if (error) throw error
}

async function handleSuccessfulLogin(user) {
  // Check if user profile exists
  const { data: profile, error } = await supabase
    .from('user_profile')
    .select('*')
    .eq('id', user.id)
    .maybeSingle()
  
  if (error) {
    console.error('Error fetching user profile:', error)
    return
  }
  
  if (!profile) {
    // Create profile if it doesn't exist
    const { error: insertError } = await supabase
      .from('user_profile')
      .insert({
        id: user.id,
        email: user.email,
        role: 'viewer',
        has_password: false,
        prefer_magic_link: true
      })
    
    if (insertError) {
      console.error('Error creating user profile:', insertError)
    }
  }
  
  // Check if we should show password setup prompt
  checkPasswordSetupPrompt(user.id, profile)
}

async function checkPasswordSetupPrompt(userId, profile) {
  // Only prompt non-admin users who don't have a password
  if (profile?.role === 'admin') return
  if (profile?.has_password) return
  
  // Check if they recently dismissed the prompt (within last 7 days)
  if (profile?.last_password_prompt) {
    const lastPrompt = new Date(profile.last_password_prompt)
    const daysSince = (Date.now() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24)
    if (daysSince < 7) return
  }
  
  // Show the prompt banner
  showPasswordSetupBanner()
}

function showPasswordSetupBanner() {
  const existingBanner = document.getElementById('password-setup-banner')
  if (existingBanner) return
  
  const banner = document.createElement('div')
  banner.id = 'password-setup-banner'
  banner.className = 'password-setup-banner'
  banner.innerHTML = `
    <div class="banner-content">
      <div class="banner-icon">💡</div>
      <div class="banner-text">
        <strong>Tip:</strong> Set a password for faster future logins (optional)
      </div>
      <div class="banner-actions">
        <button class="btn-banner-primary" id="setup-password-btn">Set Password</button>
        <button class="btn-banner-secondary" id="dismiss-banner-btn">Maybe Later</button>
      </div>
    </div>
  `
  
  // Insert after header, before content
  const appPage = document.getElementById('app-page')
  const header = appPage.querySelector('.header')
  if (header && header.nextSibling) {
    header.parentNode.insertBefore(banner, header.nextSibling)
  } else {
    appPage.insertBefore(banner, appPage.firstChild)
  }
  
  // Setup event listeners
  document.getElementById('setup-password-btn').addEventListener('click', () => {
    import('../router/router.js').then(({ navigateToPasswordSetup }) => {
      navigateToPasswordSetup()
    })
  })
  
  document.getElementById('dismiss-banner-btn').addEventListener('click', async () => {
    banner.remove()
    
    // Update last prompt timestamp
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      await supabase
        .from('user_profile')
        .update({ last_password_prompt: new Date().toISOString() })
        .eq('id', user.id)
    }
  })
}

export function initAuth() {
  // Logout button
  document.getElementById('logout-btn').addEventListener('click', handleLogout)
  
  // ADD THIS:
  // Profile button
  document.getElementById('profile-btn').addEventListener('click', () => {
    import('../router/router.js').then(({ navigateToUserProfile }) => {
      navigateToUserProfile()
    })
  })
}